import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Fade,
  IconButton,
  LinearProgress,
  Avatar,
  Chip
} from '@mui/material';
import { X, BadgeCheck } from 'lucide-react';
import { useTheme } from '@mui/material/styles';

// Toast notification that appears at the bottom right of the screen
const NotificationToast = ({ notification, onClose }) => {
  const theme = useTheme();
  const [visible, setVisible] = useState(true);
  const [progress, setProgress] = useState(100);
  const DISPLAY_DURATION = 5000; // 5 seconds display duration
  const ANIMATION_DURATION = 300; // 300ms fade out animation
  
  // Define the base URL for images
  const baseUrl = import.meta.env.VITE_APP_API_URL.slice(0, -1);

  useEffect(() => {
    let timer;
    let progressInterval;
    
    if (visible) {
      // Set up progress interval to update every 50ms
      progressInterval = setInterval(() => {
        setProgress((prevProgress) => {
          const newProgress = prevProgress - (50 / DISPLAY_DURATION * 100);
          return newProgress > 0 ? newProgress : 0;
        });
      }, 50);
      
      // Set up timer to hide the notification after display duration
      timer = setTimeout(() => {
        setVisible(false);
        setTimeout(() => {
          if (onClose) onClose();
        }, ANIMATION_DURATION); // Wait for fade out animation to complete
      }, DISPLAY_DURATION);
    }
    
    return () => {
      clearTimeout(timer);
      clearInterval(progressInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  const handleClick = () => {
    if (!notification) return;
    
    // Instead of using navigate, we'll use the global window.location
    // to change the URL, which doesn't require Router context
    let targetUrl = '/'; // default fallback
    
    // Determine target URL based on notification type
    if (notification.notification_type === 'REPORT' || (notification.data && notification.data.report_id)) {
      targetUrl = `/admin/reports/${notification.data.report_id}`;
    } else if (notification.notification_type === 'COMMENT' || 
               (notification.data && notification.data.content_type === 'comment')) {
      targetUrl = `/admin/contributions/${notification.data.comment_id}`;
    } else if (notification.data && notification.data.content_type === 'avis' && notification.data.content_id) {
      targetUrl = `/avis/${notification.data.content_id}?admin_review=true`;
    }
    
    // Navigate using window.location 
    window.location.href = targetUrl;
    
    // Close the toast
    setVisible(false);
    if (onClose) onClose();
  };

  // Don't render if no notification
  if (!notification) return null;

  // Determine icon and color based on notification type
  const isReport = notification.notification_type === 'REPORT' || 
                   (notification.data && notification.data.report_id);
  const isComment = notification.notification_type === 'COMMENT' || 
                   (notification.data && notification.data.content_type === 'comment');
  
  // Format relative time
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'À l\'instant';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `Il y a ${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `Il y a ${hours} heure${hours > 1 ? 's' : ''}`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `Il y a ${days} jour${days > 1 ? 's' : ''}`;
    }
  };
  
  // Truncate text
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };
  
  // Get full profile image URL
  const getFullProfileImageUrl = (profileImagePath) => {
    if (!profileImagePath) return null;
    return profileImagePath.startsWith('http') ? profileImagePath : `${baseUrl}${profileImagePath}`;
  };
  
  // Get content type label
  const getContentTypeLabel = (type) => {
    switch (type) {
      case 'medication':
        return 'Médicament';
      case 'expert':
        return 'Expert';
      case 'expert_opinion':
        return 'Avis médical';
      case 'institution':
        return 'Institution';
      case 'news':
        return 'Actualité';
      case 'comment':
        return 'Contribution';
      default:
        return type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Information';
    }
  };

  // Get notification color based on type
  const getNotificationColor = () => {
    if (isReport) {
      return {
        light: theme.palette.error.lighter,
        main: theme.palette.error.main,
        dark: theme.palette.error.main,
        bgHover: theme.palette.error.light
      };
    } else if (isComment) {
      return {
        light: theme.palette.info.lighter,
        main: theme.palette.info.main,
        dark: theme.palette.info.main,
        bgHover: theme.palette.info.light
      };
    } else {
      return {
        light: theme.palette.primary.lighter,
        main: theme.palette.primary.main,
        dark: theme.palette.primary.dark,
        bgHover: theme.palette.primary.light
      };
    }
  };
  
  const colors = getNotificationColor();
  const user = notification.data?.user || {};
  const reporter = notification.data?.reporter || {};
  const contentObject = notification.data?.content_object || {};
  
  // Clean notification title by removing "NOUVELLE CONTRIBUTION" or "NOUVEAU SIGNALEMENT"
  let cleanTitle = notification.title || '';
  cleanTitle = cleanTitle.replace(/^Nouvelle contribution:\s*/i, '');
  cleanTitle = cleanTitle.replace(/^Nouveau signalement:\s*/i, '');

  return (
    <Fade in={visible}>
      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          width: 320,
          zIndex: 2000,
          overflow: 'hidden',
          borderRadius: 2,
          cursor: 'pointer',
          backgroundColor: 'background.paper',
          boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.15)'
          }
        }}
        onClick={handleClick}
      >
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ 
            height: 2,
            '& .MuiLinearProgress-bar': {
              backgroundColor: colors.main
            }
          }} 
        />
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          width: '100%',
          p: 2,
          gap: 1,
          bgcolor: notification.is_read ? 'transparent' : colors.light
        }}>
          {/* Row 1: User info and timing */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar 
                src={getFullProfileImageUrl(isReport ? reporter?.profile_image : user?.profile_image_url)} 
                alt={isReport ? reporter?.username : user?.full_name}
                sx={{ width: 32, height: 32 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" fontWeight={500}>
                  {isReport ? reporter?.username : user?.full_name || 'Utilisateur'}
                </Typography>
                {!isReport && user?.is_verified && (
                  <BadgeCheck color='white' fill='#4680FE' size={16} strokeWidth={1.3} />
                )}
                {!isReport && user?.title && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    • {user.title}
                  </Typography>
                )}
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {formatRelativeTime(notification.created_at)}
            </Typography>
            
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setVisible(false);
                if (onClose) setTimeout(onClose, ANIMATION_DURATION);
              }}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'text.secondary',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
              }}
            >
              <X size={14} />
            </IconButton>
          </Box>
          
          {/* Row 2: Content title and type */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%'
          }}>
            <Typography 
              variant="body2" 
              fontWeight={600} 
              sx={{ 
                textTransform: 'uppercase',
                maxWidth: '60%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {truncateText(contentObject?.title || cleanTitle, 40)}
            </Typography>
            
            <Chip
              label={isReport ? (notification.data?.report_type || "Signalement") : getContentTypeLabel(notification.data?.content_type)}
              size="small"
              sx={{
                height: 22,
                bgcolor: isReport ? '#ffebee' : '#e3f2fd',
                color: isReport ? '#d32f2f' : '#1976d2',
                fontWeight: 500,
                fontSize: '0.7rem',
                maxWidth: '40%',
              }}
            />
          </Box>
          
          {/* Row 3: Comment content (only for comments) */}
          {isComment && notification.data?.content && (
            <Typography variant="body2" 
              sx={{ 
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {truncateText(notification.data.content, 100)}
            </Typography>
          )}
        </Box>
      </Paper>
    </Fade>
  );
};

export default NotificationToast;